import clsx from 'clsx'
import { ProductPaletteContext } from 'contexts/ProductPaletteContext'
// eslint-disable-next-line no-restricted-imports
import { getContentColourUtilityClassName } from 'lib/theme/utilityClassNames'
import { rem } from 'polished'
import React, { AnchorHTMLAttributes, Ref, useContext } from 'react'
import styled from 'styled-components'
import interactiveFocusStyle from 'styles/utils/interactiveFocusStyle'
import Clickable from '../client/components/Common/Clickable'
import ClickableLink from '../client/components/Common/Clickable/ClickableLink'
import { SVG_ICON_SIZE_CSS_VAR } from './SvgIcon'
import { BodyTextLarge, BodyTextMedium, BodyTextSmall } from './Typography/BodyText'
import { CaptionMedium } from './Typography/Caption'
import { TypographyElement } from './Typography/Typography'

const LinkElement = styled(TypographyElement)`
  display: inline;
  font-family: ${props => props.theme.font.primary.family};

  &.product-palette-tier-platinum { color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast}; }
  &:disabled { color: ${props => props.theme.palette.neutral.default.four}; }

  &.block { display: block; }

  &.has-icon-start > :first-child { margin-inline-end: ${rem(8)}; }
  &.has-icon-end > :last-child { margin-inline-start: ${rem(8)}; }

  &.underline { text-decoration: underline currentcolor; }
  &.weight-regular { font-weight: ${props => props.theme.font.primary.weight.regular}; }
  &.weight-bold { font-weight: ${props => props.theme.font.primary.weight.superBold}; }

  &.size-caption {
    ${CaptionMedium}
  }
  &.size-small {
    ${BodyTextSmall}
  }
  &.size-medium {
    ${BodyTextMedium}
  }
  &.size-large {
    ${BodyTextLarge}
    --link-icon-size: ${rem(20)};
  }

  &.wrap-no-wrap { white-space: nowrap; }
  &.wrap-word-wrap { word-break: break-word; }
  &.wrap-letter-wrap { word-break: break-all; }
  &.wrap-truncate {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  &.wrap-pre-wrap { white-space: pre-wrap; }

  > img {
    height: var(${SVG_ICON_SIZE_CSS_VAR}, ${rem(16)});
    width: var(${SVG_ICON_SIZE_CSS_VAR}, ${rem(16)});
  }

  &:not(:disabled):focus {
    ${interactiveFocusStyle}
  }

  @media print { display: none; }
`

interface Props extends AnchorHTMLAttributes<HTMLAnchorElement | HTMLButtonElement> {
  ref?: Ref<HTMLAnchorElement | HTMLButtonElement>
  colour?: App.Design.ContentColour
  /**
   * @default bold
   */
  weight?: 'regular' | 'bold'
  size?: 'caption' | 'small' | 'medium' | 'large'
  wrap?: 'no-wrap' | 'word-wrap' | 'letter-wrap' | 'truncate' | 'pre-wrap'
  to?: string
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  /**
   * @default true
   */
  underline?: boolean
  disabled?: boolean
  /**
   * Sets the text link as a 'block' element
   * this will allow things like margin to space it
   * By default, this element is an inline element
   */
  block?: boolean
}

function TextLink({
  ref,
  colour,
  className,
  size,
  weight = 'bold',
  startIcon,
  endIcon,
  children,
  underline = true,
  wrap,
  block,
  ...rest
}: Props) {
  let component: any = null
  if (rest.href || rest.to) {
    component = ClickableLink
  } else if (rest.onClick) {
    component = Clickable
  } else {
    component = 'span'
  }

  const productPalette = useContext(ProductPaletteContext)

  return <LinkElement
    {...rest}
    as={component}
    ref={ref}
    className={clsx(
      className,
      colour ? getContentColourUtilityClassName(colour) : undefined,
      `weight-${weight}`,
      `product-palette-${productPalette}`,
      size ? `size-${size}` : undefined,
      wrap ? `wrap-${wrap}` : undefined,
      {
        block,
        underline,
        'has-icon-start': !!startIcon,
        'has-icon-end': !!endIcon,
      },
    )}
  >
    {startIcon}
    {children}
    {endIcon}
  </LinkElement>
}

export default TextLink
